import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { LogOut, LayoutDashboard, Users, Briefcase, FolderKanban, BarChart3, Building2, ChevronDown, User, Settings, MessageSquare, Calendar, FileText, CheckSquare, CalendarDays, HelpCircle, Calculator } from 'lucide-react';
import { removeEmployeeToken, getEmployeeUser } from '../lib/employeeAuth';
import { toast } from 'sonner';

export default function EmployeeLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const employee = getEmployeeUser();
  const [showProfileMenu, setShowProfileMenu] = useState(false);

  const handleLogout = () => {
    removeEmployeeToken();
    toast.success('Logged out');
    navigate('/');
  };

  const menuItems = [
    { path: '/employee/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/employee/tasks', icon: CheckSquare, label: 'Tasks' },
    { path: '/employee/clients', icon: Building2, label: 'Clients' },
    { path: '/employee/leaves', icon: Calendar, label: 'Leave Management' },
    { path: '/employee/calendars', icon: CalendarDays, label: 'Calendars' },
    { path: '/employee/accounting', icon: Calculator, label: 'Accounting' },
    { path: '/employee/organisation', icon: Users, label: 'Organisation' },
    { path: '/employee/helpdesk', icon: HelpCircle, label: 'Help Desk' },
  ];

  const profileMenuItems = [
    { path: '/employee/profile', icon: User, label: 'Profile' },
    { path: '/employee/documents', icon: FileText, label: 'Documents' },
    { path: '/employee/settings', icon: Settings, label: 'Settings' },
  ];

  return (
    <div className="min-h-screen bg-charcoal-50 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-[#111111] border-r border-charcoal-800 flex flex-col">
        <div className="p-6 border-b border-charcoal-800">
          <div className="flex items-center space-x-2">
            <div className="w-10 h-10 bg-gradient-to-br from-[#FF3366] to-[#FF3366] rounded-xl flex items-center justify-center">
              <span className="text-white font-bold text-xl">G</span>
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">girgit</h1>
              <p className="text-xs text-neutral-500">Employee Portal</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {menuItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                location.pathname === item.path
                  ? 'bg-pink-600 text-white'
                  : 'text-neutral-400 hover:bg-[#111111] hover:text-white'
              }`}
            >
              <item.icon className="w-5 h-5" />
              <span className="font-medium">{item.label}</span>
            </Link>
          ))}
        </nav>

        <div className="p-4 border-t border-charcoal-800">
          <div className="text-xs text-neutral-600 text-center">
            Half Eaten Donut
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Top Bar */}
        <header className="bg-white border-b border-charcoal-200">
          <div className="px-8 py-4 flex justify-between items-center">
            <h2 className="text-xl font-semibold text-charcoal-900">
              {menuItems.find(m => m.path === location.pathname)?.label || 'Employee Portal'}
            </h2>
            <div className="relative">
              <button
                onClick={() => setShowProfileMenu(!showProfileMenu)}
                className="flex items-center gap-3 px-4 py-2 bg-charcoal-50 rounded-xl hover:bg-charcoal-100 transition-colors border border-charcoal-200"
                data-testid="profile-menu-button"
              >
                <div className="text-right">
                  <p className="text-sm font-medium text-charcoal-900">{employee?.personal?.full_name || employee?.work_email}</p>
                  <p className="text-xs text-neutral-600">{employee?.employment?.designation}</p>
                </div>
                <ChevronDown className="w-4 h-4 text-charcoal-600" />
              </button>

              {showProfileMenu && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-charcoal-200 py-2 z-50">
                  {profileMenuItems.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setShowProfileMenu(false)}
                      className="flex items-center gap-3 px-4 py-2 hover:bg-charcoal-50 transition-colors"
                    >
                      <item.icon className="w-4 h-4 text-charcoal-600" />
                      <span className="text-sm text-charcoal-700">{item.label}</span>
                    </Link>
                  ))}
                  <div className="border-t border-charcoal-200 my-2"></div>
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-4 py-2 hover:bg-red-50 transition-colors text-red-600"
                    data-testid="logout-button"
                  >
                    <LogOut className="w-4 h-4" />
                    <span className="text-sm font-medium">Logout</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto bg-charcoal-50">
          {children}
        </main>
      </div>
    </div>
  );
}
